import { useEffect, useState } from 'react';

import {
  useAddResiduals,
  useContract,
  useImportResidualsCsv,
  useOverrideResiduals,
  useResidualMetrics,
  useResiduals,
} from '../../api/hooks';
import type { ManualResidualInput, ResidualView } from '../../api/types';
import {
  Button,
  DataTable,
  DateInput,
  FormField,
  TextInput,
  useToast,
} from '../../components/DesignSystem';
import type { DataTableColumn } from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';
import { findGoverningPolicy } from '../../lib/governingPolicies';
import { formatCurrency, formatNumber, formatPercent } from '../../lib/format';
import { CsvImportBox } from './CsvImportBox';
import { EMPTY_PARTY_VALUE, PartyField, usePartyResolver } from './PartyField';
import type { PartyFieldValue } from './PartyField';

const RESIDUAL_COLUMNS: DataTableColumn<ResidualView>[] = [
  { key: 'residualType', header: 'Type' },
  { key: 'partyName', header: 'Party' },
  { key: 'rate', header: 'Rate', align: 'right', render: (row) => formatPercent(row.rate) },
  { key: 'amount', header: 'Amount', align: 'right', render: (row) => formatCurrency(row.amount) },
  { key: 'effectiveFrom', header: 'Effective From' },
  { key: 'source', header: 'Source' },
];

interface DefaultResidualCardProps {
  contractId: string;
  title: string;
  residualType: string;
  defaultRate: number | undefined;
  territory: string | undefined;
}

/** One policy-driven residual default (Writers Residual (WGGB) / Performer Residual (PAC)). */
function DefaultResidualCard({
  contractId,
  title,
  residualType,
  defaultRate,
  territory,
}: DefaultResidualCardProps) {
  const { showToast } = useToast();
  const addResiduals = useAddResiduals();
  const [rate, setRate] = useState(defaultRate?.toString() ?? '');

  useEffect(() => {
    setRate(defaultRate?.toString() ?? '');
  }, [defaultRate]);

  async function handleSave() {
    const numericRate = Number(rate);
    if (!Number.isFinite(numericRate) || numericRate <= 0) {
      showToast('Enter a positive rate', 'error');
      return;
    }

    try {
      await addResiduals.mutateAsync({
        contractId,
        residuals: [{ residualType, rate: numericRate }],
      });
      showToast(`${title} saved`);
    } catch (error) {
      showToast(errorMessage(error, `Could not save ${title}`), 'error');
    }
  }

  return (
    <div className="rfp-card" style={{ flex: 1 }}>
      <h3 className="rfp-card__title">{title}</h3>
      <p className="rfp-card__caption">
        {territory ? `${territory} only` : 'Applies to all territories'} — deducted from gross
        revenue before backend distribution.
      </p>
      <div className="rfp-row" style={{ marginTop: 12 }}>
        <FormField htmlFor={`${residualType}-rate`} label="Rate %">
          <TextInput
            id={`${residualType}-rate`}
            min="0"
            onChange={(event) => setRate(event.target.value)}
            step="0.01"
            type="number"
            value={rate}
          />
        </FormField>
        <Button disabled={addResiduals.isPending} onClick={handleSave} type="button">
          Save
        </Button>
      </div>
    </div>
  );
}

interface ResidualDefaultsSectionProps {
  contractId: string;
}

/** Residual Defaults (applied to new parties) + the long tail of non-standard residuals. */
export function ResidualDefaultsSection({ contractId }: ResidualDefaultsSectionProps) {
  const { showToast } = useToast();
  const contract = useContract(contractId);
  const residuals = useResiduals(contractId);
  const residualMetrics = useResidualMetrics(contractId);
  const importCsv = useImportResidualsCsv();
  const addResiduals = useAddResiduals();
  const overrideResiduals = useOverrideResiduals();
  const resolveParty = usePartyResolver();

  const policy = findGoverningPolicy(contract.data?.governingPolicy);

  const [otherOpen, setOtherOpen] = useState(false);
  const [party, setParty] = useState<PartyFieldValue>(EMPTY_PARTY_VALUE);
  const [residualType, setResidualType] = useState('');
  const [rate, setRate] = useState('');
  const [amount, setAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  function buildResidualInput(): ManualResidualInput | null {
    if (residualType.trim() === '') {
      showToast('Enter a residual type (e.g. music, composer)', 'error');
      return null;
    }

    const hasRate = rate.trim() !== '';
    const hasAmount = amount.trim() !== '';

    if (hasRate === hasAmount) {
      showToast('Provide exactly one of rate or amount', 'error');
      return null;
    }

    return {
      residualType: residualType.trim(),
      ...(hasRate ? { rate: Number(rate) } : { amount: Number(amount) }),
      ...(effectiveFrom !== '' ? { effectiveFrom } : {}),
    };
  }

  async function submitResidual(mode: 'add' | 'override') {
    const input = buildResidualInput();
    if (input === null || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const partyId = await resolveParty(party);
      const payload = { ...input, ...(partyId !== undefined ? { partyId } : {}) };

      if (mode === 'add') {
        await addResiduals.mutateAsync({ contractId, residuals: [payload] });
        showToast('Residual added');
      } else {
        await overrideResiduals.mutateAsync({ contractId, residuals: [payload] });
        showToast('Residuals overridden — previous entries replaced');
      }

      setParty(EMPTY_PARTY_VALUE);
      setResidualType('');
      setRate('');
      setAmount('');
      setEffectiveFrom('');
    } catch (error) {
      showToast(errorMessage(error, 'Could not save residual'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="rfp-card">
      <div className="rfp-card__head">
        <div>
          <h2 className="rfp-card__title">Residual Defaults (applied to new parties)</h2>
          <p className="rfp-card__caption">
            These values pre-fill new parties. Each party's own row in the Back End Share table
            decides what is actually deducted.
          </p>
        </div>
      </div>

      <div className="rfp-row">
        <DefaultResidualCard
          contractId={contractId}
          defaultRate={policy?.writerRate}
          residualType="Writers Residual (WGGB)"
          territory={policy?.territory}
          title="Writers Residual (WGGB)"
        />
        <DefaultResidualCard
          contractId={contractId}
          defaultRate={policy?.performerRate}
          residualType="Performer Residual (PAC)"
          territory={policy?.territory}
          title="Performer Residual (PAC)"
        />
      </div>

      <CsvImportBox
        entity="residuals"
        onImport={(csvText, fileName) => importCsv.mutateAsync({ contractId, csvText, fileName })}
        successMessage="Residuals imported from CSV"
      />

      <div className="rfp-metric-strip">
        <div className="rfp-metric-strip__item">
          <span className="rfp-metric-strip__label">Entries</span>
          <span className="rfp-metric-strip__value">
            {formatNumber(residualMetrics.data?.count)}
          </span>
        </div>
        <div className="rfp-metric-strip__item">
          <span className="rfp-metric-strip__label">Total Rate</span>
          <span className="rfp-metric-strip__value">
            {formatPercent(residualMetrics.data?.totalRatePercent)}
          </span>
        </div>
        <div className="rfp-metric-strip__item">
          <span className="rfp-metric-strip__label">Fixed Amounts</span>
          <span className="rfp-metric-strip__value">
            {formatCurrency(residualMetrics.data?.totalFixedAmount)}
          </span>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Button onClick={() => setOtherOpen((open) => !open)} type="button" variant="secondary">
          {otherOpen ? '▾' : '▸'} Other residuals ({formatNumber(residuals.data?.length ?? 0)})
        </Button>
      </div>

      {otherOpen && (
        <>
          {residuals.isLoading ? (
            <p className="rfp-loading">Loading residuals…</p>
          ) : (
            <DataTable
              columns={RESIDUAL_COLUMNS}
              emptyMessage="No residuals yet"
              rowKey={(row) => row.id}
              rows={residuals.data ?? []}
            />
          )}

          <div className="rfp-row" style={{ marginTop: 16 }}>
            <FormField htmlFor="residual-type" label="Residual Type">
              <TextInput
                id="residual-type"
                onChange={(event) => setResidualType(event.target.value)}
                placeholder="e.g. music"
                value={residualType}
              />
            </FormField>
            <PartyField allowNone id="residual-party" onChange={setParty} value={party} />
            <FormField htmlFor="residual-rate" label="Rate %">
              <TextInput
                id="residual-rate"
                min="0"
                onChange={(event) => setRate(event.target.value)}
                placeholder="e.g. 2.5"
                step="0.01"
                type="number"
                value={rate}
              />
            </FormField>
            <FormField htmlFor="residual-amount" label="Amount £">
              <TextInput
                id="residual-amount"
                min="0"
                onChange={(event) => setAmount(event.target.value)}
                placeholder="e.g. 500"
                step="0.01"
                type="number"
                value={amount}
              />
            </FormField>
            <FormField htmlFor="residual-from" label="Effective From">
              <DateInput
                id="residual-from"
                onChange={(event) => setEffectiveFrom(event.target.value)}
                value={effectiveFrom}
              />
            </FormField>
            <Button disabled={isSubmitting} onClick={() => submitResidual('add')} type="button">
              Add
            </Button>
            <Button
              disabled={isSubmitting}
              onClick={() => submitResidual('override')}
              type="button"
              variant="secondary"
            >
              Override all
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
